import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Sidebar, Menu, MenuItem, menuClasses } from "react-pro-sidebar";
import { Tooltip, Typography, Popover, Box, MenuItem as MuiMenuItem } from "@mui/material"
import AssessmentIcon from "@mui/icons-material/Assessment";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import { Icon } from "../icons";
import {
  setKpiStatus,
  setGraphStatus,
  setDateSystemStatus,
  setDisasterStatus,
  setSvResultStatus,
  setProductivityStatus,
  setDtiInfoStatus,
  setDtInfoMenuOpen,
  setDtInfoMode,
} from "../../slice/authSlice";
import { hexToRGBA } from "../../utils/hex-to-rgba";
import "./SidebarStyles.css";
import axios from "axios";
import { axiosPrivate } from "../../api/axios";
import API_URL from "../../utils/API_URL";
import APS_URL from "../../utils/APS_URL";
import { useParams } from "react-router-dom";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DescriptionIcon from "@mui/icons-material/Description";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

// 다크 테마 값 직접 정의
const darkTheme = {
  palette: {
    mode: "dark",
    primary: {
      main: "#7367F0",
      light: "#8F85F3",
      dark: "#675DD8",
    },
    background: {
      default: "#25293C",
      paper: "#2F3349",
    },
    text: {
      primary: "rgb(225 222 245 / 0.9)",
      secondary: "rgb(225 222 245 / 0.7)",
    },
    divider: "rgb(225 222 245 / 0.12)",
    action: {
      active: "rgb(225 222 245 / 0.6)",
      hover: "rgb(225 222 245 / 0.06)",
      selected: "rgb(225 222 245 / 0.08)",
      disabled: "rgb(225 222 245 / 0.3)",
      disabledBackground: "rgb(225 222 245 / 0.16)",
      focus: "rgb(225 222 245 / 0.1)",
    },
    customColors: {
      bodyBg: "#25293C",
      chatBg: "#202534",
      greyLightBg: "#353A52",
      inputBorder: "rgb(225 222 245 / 0.22)",
      tableHeaderBg: "#2F3349",
      tooltipText: "#2F3349",
      trackBg: "#3A3F57",
    },
  },
};

// 점멸 효과 및 전환 애니메이션을 위한 CSS Keyframes 추가
const blinkingEffect = `
  @keyframes blinking {
    0% { opacity: 1; }
    50% { opacity: 0.3; }
    100% { opacity: 1; }
  }
  @keyframes fadeSlideIn {
    from { opacity: 0; transform: translateX(12px); }
    to { opacity: 1; transform: translateX(0); }
  }
`;

const languageIconMap = {
  ko: "southKoreaIcon",
  en: "usaIcon",
  jp: "japanIcon",
};

const languageNameMap = {
  ko: "sidebars.dt.korean",
  en: "sidebars.dt.english",
  jp: "sidebars.dt.japanese",
};

function SideBar({ pageType = "LE", handleLogout = () => {}, onLanguageChange = () => {}, onDtInfoModeChange = () => {} }) {
  const [hasImage] = React.useState(false);
  const [toggled, setToggled] = React.useState(false);
  const { t, i18n } = useTranslation();

  const [anchorEl, setAnchorEl] = useState(null); // Popover 앵커 엘리먼트 상태
  const openPopover = Boolean(anchorEl);

  const { 
    graphStatus, 
    kpiStatus, 
    dateSystemStatus,
    disasterStatus,
    svResultStatus,
    productivityStatus,
    dtiInfoStatus,
    dtInfoMenuOpen,
    dtInfoMode,
  } = useSelector((state) => state.auth);

  const { group_code, project_code } = useParams();
  const [currentDateName, setCurrentDateName] = useState("");
  // 시스템 활성화 상태 관리
  const [systemStatus, setSystemStatus] = useState({ SAFE: false, SV: false });

  const dispatch = useDispatch();

  // 시스템 활성화 상태 조회
  useEffect(() => {
    const fetchSystemStatus = async () => {
      try {
        const response = await axiosPrivate.post("/project/getSystemStatus", {
          group_code,
          project_code,
        });
        setSystemStatus(response.data);
      } catch (error) {
        console.error("시스템 상태 조회 실패:", error);
      }
    };
    if (group_code && project_code) {
      fetchSystemStatus();
    }
  }, [group_code, project_code]);

  useEffect(() => {
    async function fetchData() {
      try {
        const result = await axios.get(
          APS_URL +
            `/api/dt/getSystem?group_code=${group_code}&project_code=${project_code}`
        );
        if (result.data.key === "use_custom") {
          setCurrentDateName(result.data.value);
        } else if (result.data.key === "use_current") {
          const today = new Date();
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, "0");
          const day = String(today.getDate()).padStart(2, "0");
          const formattedDate = `${year}-${month}-${day}`;
          setCurrentDateName(formattedDate);
        }

        // 위험성 평가 DT 백엔드 데이터 처리
        // await axios.put(
        //   API_URL + `/risk/${group_code}/${project_code}/putRiskSyncDate`,
        //   result.data
        // );
      } catch (e) {
        console.log(e);
      }
    }
    fetchData();
  }, [group_code, project_code, dateSystemStatus]);

  const handleHomeStatus = () => {
    const url = `${window.location.origin}/projects`;
    const windowName = `projects_tab`;
    const newWindow = window.open(url, windowName);
    if (newWindow) {
      newWindow.focus();
    }
  };

  const handleSvResultStatus = () => {
    dispatch(setSvResultStatus(!svResultStatus));
  };

  const handleGraphStatus = () => {
    if (disasterStatus.isActive) {
      toast.warn("재난 상황 대응 중에는 해당 기능을 사용할 수 없습니다.");
      return;
    }
    dispatch(setGraphStatus(!graphStatus));
  };
  const handleKpiCardStatus = () => {
    dispatch(setKpiStatus(!kpiStatus));
  };
  const handleDateSystemStatus = () => {
    dispatch(setDateSystemStatus(!dateSystemStatus));
  };

  const handleFireAlarmStatus = async () => {
    if (!systemStatus.SAFE) return; // SAFE 미사용 시 동작 안 함
    if (disasterStatus.isActive) {
      try {
        await axios.post(API_URL + "/scenario/resolve", {
          group_code,
          project_code,
        });
      } catch (error) {
        toast.error(error.response?.data || "상황 종료에 실패했습니다.");
      }
    } else {
      try {
        await axios.post(API_URL + "/scenario/start", {
          group_code,
          project_code,
          scenario_category: 1,
        });
      } catch (error) {
        toast.error(error.response?.data || "시나리오 시작에 실패했습니다.");
      }
    }
  };

  const handleProductivityStatus = () => {
    if (!systemStatus.SV) return; // SV 미사용 시 동작 안 함
    if (disasterStatus.isActive) {
      toast.warn("재난 상황 대응 중에는 해당 기능을 사용할 수 없습니다.");
      return;
    }
    dispatch(setProductivityStatus(!productivityStatus));
  };

  const handleDtiInfoStatus = () => {
    dispatch(setDtInfoMenuOpen(true));
    dispatch(setDtInfoMode(0));
    try {
      onDtInfoModeChange(0);
    } catch (e) {
      // no-op
    }
  };

  const handleDtInfoBack = () => {
    dispatch(setDtInfoMenuOpen(false));
  };

  const handleSelectDtInfoMode = (mode) => {
    dispatch(setDtInfoMode(mode));
    try {
      onDtInfoModeChange(mode);
    } catch (e) {
      // no-op
    }
  };

  const sidebarTheme = {
    sidebar: {
      backgroundColor: darkTheme.palette.background.default,
      color: darkTheme.palette.text.primary,
    },
    menu: {
      menuContent: darkTheme.palette.background.paper,
      icon: darkTheme.palette.text.secondary,
      disabled: {
        color: darkTheme.palette.action.disabled,
      },
    },
  };

  const menuItemStyles = {
    root: {
      fontSize: "13px",
      fontWeight: 400,
    },
    icon: {
      color: sidebarTheme.menu.icon,
      "& svg": {
        width: "20px",
        height: "20px",
      },
    },
    button: {
      [`&.${menuClasses.disabled}`]: {
        color: sidebarTheme.menu.disabled.color,
      },
      "&:hover": {
        backgroundColor: darkTheme.palette.action.hover,
        color: darkTheme.palette.primary.main,
      },
      height: "auto",
      padding: "10px 0",
    },
    label: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "4px",
    },
  };

  // Popover를 열고 닫는 핸들러
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // 언어 변경 핸들러
  const handleChangeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    handleClose(); // Popover 닫기
    try {
      onLanguageChange(lang);
    } catch (e) {
      // no-op
    }
  };

  const currentLanguage = i18n.language;

  return (
    <>
      <style>{blinkingEffect}</style>
      <Sidebar
        toggled={toggled}
        onBackdropClick={() => setToggled(false)}
        backgroundColor={hexToRGBA(
          sidebarTheme.sidebar.backgroundColor,
          hasImage ? 0.9 : 1
        )}
        rootStyles={{
          height: "100%",
          color: sidebarTheme.sidebar.color,
          boxSizing: "content-box",
          width: "80px",
          minWidth: "80px",
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 100,
          boxShadow: "0px 2px 8px rgba(19, 17, 32, 0.2)",
          borderRight: `1px solid ${darkTheme.palette.divider}`,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <div
            style={{
              minHeight: "64px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "14px",
            }}
          >
            <img
              src="/images/ese_logo.png"
              alt="logo"
              style={{ width: "60px" }}
            />
          </div>
          <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
            <div
              style={{
                marginBottom: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                fontWeight={600}
                style={{
                  fontSize: "12px",
                  opacity: 0.7,
                  letterSpacing: "0.5px",
                  color: darkTheme.palette.text.secondary,
                }}
              >
                {t("sidebars.dt.navigate")}
              </Typography>
            </div>
            <div style={{ animation: "fadeSlideIn 220ms ease" }}>
              <Menu menuItemStyles={menuItemStyles}>
                <MenuItem onClick={handleHomeStatus}>
                  <Icon
                    name="homeIcon"
                    style={{
                      width: "24px",
                      height: "24px",
                      color: "rgba(225, 222, 245, 0.6)",
                    }}
                  />
                  <Typography
                    variant="caption"
                    style={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: darkTheme.palette.text.primary,
                    }}
                  >
                    {t("sidebars.dt.home")}
                  </Typography>
                </MenuItem>
              </Menu>
            </div>
            {pageType === "SV" ? (
              <>
                <div
                  style={{
                    marginTop: "8px",
                    marginBottom: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    fontWeight={600}
                    style={{
                      fontSize: "12px",
                      opacity: 0.7,
                      letterSpacing: "0.5px",
                      color: darkTheme.palette.text.secondary,
                    }}
                  >
                    {t("sidebars.sv.svMenuTitle")}
                  </Typography>
                </div>
                <div style={{ animation: "fadeSlideIn 220ms ease" }}>
                  <Menu menuItemStyles={menuItemStyles}>
                    <MenuItem onClick={handleSvResultStatus}>
                      <AssessmentIcon
                        style={{
                          width: "24px",
                          height: "24px",
                          color: svResultStatus
                            ? darkTheme.palette.primary.main
                            : "rgba(225, 222, 245, 0.6)",
                        }}
                      />
                      <Typography
                        variant="caption"
                        style={{
                          fontSize: "11px",
                          fontWeight: 600,
                          color: darkTheme.palette.text.primary,
                        }}
                      >
                        {t("sidebars.dt.resultAnalysis")}
                      </Typography>
                    </MenuItem>
                    <MenuItem disabled>
                      <DescriptionIcon
                        style={{
                          width: "24px",
                          height: "24px",
                          color: "rgba(225, 222, 245, 0.3)",
                        }}
                      />
                      <Typography
                        variant="caption"
                        style={{
                          fontSize: "11px",
                          fontWeight: 600,
                          color: darkTheme.palette.text.primary,
                        }}
                      >
                        {t("sidebars.dt.report")}
                      </Typography>
                    </MenuItem>
                  </Menu>
                </div>
              </>
            ) : (
              <>
                {!dtInfoMenuOpen ? (
                  <>
                    <div
                      style={{
                        marginTop: "8px",
                        marginBottom: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        fontWeight={600}
                        style={{
                          fontSize: "12px",
                          opacity: 0.7,
                          letterSpacing: "0.5px",
                          color: darkTheme.palette.text.secondary,
                        }}
                      >
                        {t("sidebars.dt.dtMenuTitle")}
                      </Typography>
                    </div>
                    <div style={{ animation: "fadeSlideIn 220ms ease" }}>
                      <Menu menuItemStyles={menuItemStyles}>
                        <MenuItem onClick={handleKpiCardStatus}>
                          <LightbulbIcon
                            style={{
                              width: "24px",
                              height: "24px",
                              color: kpiStatus
                                ? darkTheme.palette.primary.main
                                : "rgba(225, 222, 245, 0.6)",
                            }}
                          />
                          <Typography
                            variant="caption"
                            style={{
                              fontSize: "11px",
                              fontWeight: 600,
                              color: darkTheme.palette.text.primary,
                            }}
                          >
                            {t("sidebars.dt.kpi")}
                          </Typography>
                        </MenuItem>
                        <MenuItem onClick={handleGraphStatus}>
                          <AssessmentIcon
                            style={{
                              width: "24px",
                              height: "24px",
                              color: graphStatus
                                ? darkTheme.palette.primary.main
                                : "rgba(225, 222, 245, 0.6)",
                            }}
                          />
                          <Typography
                            variant="caption"
                            style={{
                              fontSize: "11px",
                              fontWeight: 600,
                              color: darkTheme.palette.text.primary,
                            }}
                          >
                            {t("sidebars.dt.graph")}
                          </Typography>
                        </MenuItem>
                        <MenuItem onClick={handleDateSystemStatus}>
                          <AccessTimeIcon
                            style={{
                              width: "24px",
                              height: "24px",
                              color: dateSystemStatus
                                ? darkTheme.palette.primary.main
                                : "rgba(225, 222, 245, 0.6)",
                            }}
                          />
                          <Typography
                            variant="caption"
                            style={{
                              fontSize: "11px",
                              fontWeight: 600,
                              color: darkTheme.palette.text.primary,
                            }}
                          >
                            {currentDateName}
                          </Typography>
                        </MenuItem>
                        <Tooltip 
                          title={!systemStatus.SV ? "위험성 평가 비활성 상태" : ""}
                          placement="right"
                          arrow
                          disableHoverListener={systemStatus.SV}
                        >
                          <div style={{ display: 'flex' }}>
                          <MenuItem 
                            onClick={handleFireAlarmStatus}
                            disabled={!systemStatus.SAFE}
                          >
                            <Icon
                              name="fireIcon"
                              style={{
                                width: "24px",
                                height: "24px",
                                color: !systemStatus.SAFE 
                                  ? "rgba(225, 222, 245, 0.3)" 
                                  : disasterStatus.isActive
                                    ? "#f44336"
                                    : "rgba(225, 222, 245, 0.6)",
                                animation: disasterStatus.isActive
                                  ? "blinking 1.5s infinite"
                                  : "none",
                              }}
                            />
                            <Typography
                              variant="caption"
                              style={{
                                fontSize: "11px",
                                fontWeight: 600,
                                color: !systemStatus.SAFE ? darkTheme.palette.action.disabled : darkTheme.palette.text.primary,
                              }}
                            >
                              {disasterStatus.isActive
                                ? t("sidebars.dt.fireAlarmStop")
                                : t("sidebars.dt.fireAlarmStart")}
                            </Typography>
                          </MenuItem>
                          </div>
                        </Tooltip>
                        <Tooltip title={!systemStatus.SV ? "SimView 비활성 상태" : ""}
                          placement="right"
                          arrow
                          disableHoverListener={systemStatus.SV}
                        >
                          <div style={{ display: 'flex' }}>
                            <MenuItem 
                              onClick={handleProductivityStatus}
                              disabled={!systemStatus.SV}
                            >
                              <Icon
                                name="trendUpIcon"
                                style={{
                                  width: "24px",
                                  height: "24px",
                                  color: !systemStatus.SV
                                    ? "rgba(225, 222, 245, 0.3)"
                                    : productivityStatus
                                      ? darkTheme.palette.primary.main
                                      : "rgba(225, 222, 245, 0.6)",
                                }}
                              />
                              <Typography
                                variant="caption"
                                style={{
                                  fontSize: "11px",
                                  fontWeight: 600,
                                  color: !systemStatus.SV ? darkTheme.palette.action.disabled : darkTheme.palette.text.primary,
                                }}
                              >
                                {t("sidebars.dt.productivity")}
                              </Typography>
                            </MenuItem>
                          </div>
                        </Tooltip>
                      </Menu>
                    </div>
                    <div
                      style={{
                        marginTop: "8px",
                        marginBottom: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        fontWeight={600}
                        style={{
                          fontSize: "12px",
                          opacity: 0.7,
                          letterSpacing: "0.5px",
                          color: darkTheme.palette.text.secondary,
                        }}
                      >
                        {t("sidebars.dt.dtInfoMenuTitle")}
                      </Typography>
                    </div>
                    <div style={{ animation: "fadeSlideIn 220ms ease" }}>
                      <Menu menuItemStyles={menuItemStyles}>
                        <MenuItem onClick={handleDtiInfoStatus}>
                          <Icon
                            name="messageIcon"
                            style={{
                              width: "24px",
                              height: "24px",
                              color: dtInfoMenuOpen
                                ? darkTheme.palette.primary.main
                                : "rgba(225, 222, 245, 0.6)",
                            }}
                          />
                          <Typography
                            variant="caption"
                            style={{
                              fontSize: "11px",
                              fontWeight: 600,
                              color: darkTheme.palette.text.primary,
                            }}
                          >
                            {t("sidebars.dt.dtInfoMenuOption")}
                          </Typography>
                        </MenuItem>
                      </Menu>
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        marginTop: "8px",
                        marginBottom: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        fontWeight={600}
                        style={{
                          fontSize: "12px",
                          opacity: 0.7,
                          letterSpacing: "0.5px",
                          color: darkTheme.palette.text.secondary,
                        }}
                      >
                        {t("sidebars.dt.dtInfoMenuTitle")}
                      </Typography>
                    </div>
                    <div style={{ animation: "fadeSlideIn 220ms ease" }}>
                      <Menu menuItemStyles={menuItemStyles}>
                        {[0, 1, 2, 3, 4, 5].map((mode) => (
                          <MenuItem key={mode} onClick={() => handleSelectDtInfoMode(mode)}>
                            <Icon
                              name="messageIcon"
                              style={{
                                width: "24px",
                                height: "24px",
                                color:
                                  dtInfoMode === mode
                                    ? darkTheme.palette.primary.main
                                    : "rgba(225, 222, 245, 0.6)",
                              }}
                            />
                            <Typography
                              variant="caption"
                              style={{
                                fontSize: "11px",
                                fontWeight: 600,
                                color: darkTheme.palette.text.primary,
                              }}
                            >
                              {mode === 0 ? "기본" : `정보${mode}`}
                            </Typography>
                          </MenuItem>
                        ))}
                      </Menu>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "4px 0",
                        marginBottom: "4px",
                      }}
                    >
                      <button
                        onClick={handleDtInfoBack}
                        style={{
                          background: "transparent",
                          border: `1px solid ${darkTheme.palette.divider}`,
                          borderRadius: "4px",
                          color: darkTheme.palette.text.primary,
                          cursor: "pointer",
                          fontSize: "18px",
                          lineHeight: "18px",
                          padding: "4px 8px",
                        }}
                        aria-label="back"
                        title={t("sidebars.dt.navigate")}
                      >
                        <Icon name="arrowLeftIcon" style={{ width: "18px", height: "18px" }} />
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          {/* 하단 공통 메뉴 */}
          <div style={{ borderTop: `1px solid ${darkTheme.palette.divider}` }}>
            <Menu menuItemStyles={menuItemStyles}>
              {/* 언어 선택 메뉴를 Popover로 변경 */}
              <MenuItem onClick={handleClick}>
                <Icon
                  name={languageIconMap[currentLanguage]}
                  style={{
                    width: "24px",
                    height: "24px",
                    color: darkTheme.palette.primary.main,
                  }}
                />
                <Typography
                  variant="caption"
                  style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: darkTheme.palette.text.primary,
                  }}
                >
                  {t(languageNameMap[currentLanguage])}
                </Typography>
              </MenuItem>
              <Popover
                open={openPopover}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                PaperProps={{
                  style: {
                    backgroundColor: darkTheme.palette.background.default,
                    border: `1px solid ${darkTheme.palette.divider}`,
                    borderRadius: '4px',
                    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.25)',
                  }
                }}
              >
                <Box sx={{ p: 1 }}>
                  {Object.keys(languageIconMap).map((lang) => (
                    <div key={lang}>
                      <MuiMenuItem
                        onClick={() => handleChangeLanguage(lang)}
                        sx={{
                          "&.MuiMenuItem-root": {
                            ...menuItemStyles.button,
                            padding: "8px 12px",
                            marginInline: "0px",
                          },
                          "&:hover": {
                            backgroundColor: darkTheme.palette.action.hover,
                          },
                        }}
                      >
                        <Icon
                          name={languageIconMap[lang]}
                          style={{
                            width: "20px",
                            height: "20px",
                            marginRight: "8px",
                            color: "rgba(225, 222, 245, 0.6)",
                          }}
                        />
                        <Typography variant="body2" style={{ fontSize: "11px", fontWeight: 600, color: darkTheme.palette.text.primary }}>
                          {t(languageNameMap[lang])}
                        </Typography>
                      </MuiMenuItem>
                    </div>
                  ))}
                </Box>
              </Popover>
              <MenuItem onClick={handleLogout}>
                <Icon
                  name="logoutIcon"
                  style={{ width: "24px", height: "24px", color: "rgba(225, 222, 245, 0.6)" }}
                />
                <Typography variant="caption" style={{ fontSize: "11px", fontWeight: 600, color: darkTheme.palette.text.primary }}>
                  {t("sidebars.common.logout")}
                </Typography>
              </MenuItem>
            </Menu>
          </div>
        </div>
      </Sidebar>
    </>
  );
}

export default SideBar;